import CommonApi from "../common/CommonApi";
import axios from "axios";
import SuccessResponse from "../dto/common/SuccessResponse";
import CommonRequestBodyDto from "../dto/common/CommonRequestBodyDto";
import NoticeDto from "../dto/settings/NoticeDto";
import NoticeSaveReqDto from "../dto/settings/NoticeSaveReqDto";
import NoticeUpdateReqDto from "../dto/settings/NoticeUpdateReqDto";
import GuideDto from "../dto/settings/GuideDto";
import GuideSaveReqDto from "../dto/settings/GuideSaveReqDto";
import GuideUpdateReqDto from "../dto/settings/GuideUpdateReqDto";
import CoursePriceUpdateReqDto from "../dto/settings/CoursePriceUpdateReqDto";

class RealSettingsApi extends CommonApi {
    // ===================== Notice (country-based, single) =====================

    async getNotice({
        country,
        targetClientType,
    }: {
        country: string;
        targetClientType: "MANAGER" | "CUSTOMER" | "ALL";
    }): Promise<SuccessResponse<NoticeDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/setting/notice`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<NoticeDto>>(`${this.BASE_SERVICE_URL}/setting/notice`, {
            headers: baseHeaders,
            params: { country, "taget-client-type": targetClientType },
        });

        return response.data;
    }

    async saveNotice({
        country,
        targetClientType,
        text,
        linkurl,
        content,
    }: NoticeSaveReqDto): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();
        const isMultipart = !!content;

        const params: Record<string, any> = { country, "taget-client-type": targetClientType };
        if (text !== undefined) params.text = text;
        if (linkurl !== undefined) params.linkurl = linkurl;

        await axios.options(`${this.BASE_SERVICE_URL}/setting/notice`, { headers: baseHeaders });

        let body: any = undefined;
        const reqHeaders: Record<string, any> = { ...baseHeaders };
        if (isMultipart) {
            const formData = new FormData();
            formData.append("content", content as File);
            body = formData;
            reqHeaders["Content-Type"] = "multipart/form-data";
        }

        const response = await axios.post<SuccessResponse<object>>(`${this.BASE_SERVICE_URL}/setting/notice`, body, {
            headers: reqHeaders,
            params,
        });

        return response.data;
    }

    async updateNotice({
        country,
        targetClientType,
        text,
        linkurl,
        content,
    }: NoticeUpdateReqDto): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();
        const isMultipart = !!content;

        const params: Record<string, any> = { country, "taget-client-type": targetClientType };
        if (text !== undefined) params.text = text;
        if (linkurl !== undefined) params.linkurl = linkurl;

        await axios.options(`${this.BASE_SERVICE_URL}/setting/notice`, { headers: baseHeaders });

        let body: any = undefined;
        const reqHeaders: Record<string, any> = { ...baseHeaders };
        if (isMultipart) {
            const formData = new FormData();
            formData.append("content", content as File);
            body = formData;
            reqHeaders["Content-Type"] = "multipart/form-data";
        }

        const response = await axios.put<SuccessResponse<object>>(`${this.BASE_SERVICE_URL}/setting/notice`, body, {
            headers: reqHeaders,
            params,
        });

        return response.data;
    }

    async deleteNotice({
        country,
        targetClientType,
    }: {
        country: string;
        targetClientType: "MANAGER" | "CUSTOMER" | "ALL";
    }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/setting/notice`, { headers: baseHeaders });

        const response = await axios.delete<SuccessResponse<object>>(`${this.BASE_SERVICE_URL}/setting/notice`, {
            headers: baseHeaders,
            params: { country, "taget-client-type": targetClientType },
        });

        return response.data;
    }

    // ===================== Guide =====================

    async getGuideList(params: {
        guideId?: string;
        title?: string;
        subtitle?: string;
        modelType?: string;
        language?: string;
        guideType?: string;
        deviceType?: string;
        seq?: string;
        code?: string;
        page?: number;
        pageSize?: number;
    }): Promise<SuccessResponse<GuideDto[]> & { count?: number; totalCount?: number }> {
        const baseHeaders = this.makeBaseHeader();

        const query: Record<string, any> = {};
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== "") query[k] = v;
        });

        await axios.options(`${this.BASE_SERVICE_URL}/setting/guide`, { headers: baseHeaders });

        const response = await axios.get<SuccessResponse<GuideDto[]> & { count?: number; totalCount?: number }>(
            `${this.BASE_SERVICE_URL}/setting/guide`,
            {
                headers: baseHeaders,
                params: query,
            }
        );

        return response.data;
    }

    async saveGuide({
        title,
        subtitle,
        modelType,
        code,
        language,
        guideType,
        deviceType,
        seq,
        content,
    }: GuideSaveReqDto): Promise<SuccessResponse<[boolean, number]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/setting/guide`, { headers: baseHeaders });

        const response = await axios.post<SuccessResponse<[boolean, number]>>(
            `${this.BASE_SERVICE_URL}/setting/guide`,
            content,
            {
                headers: { ...baseHeaders, "Content-Type": "text/html" },
                params: { title, subtitle, modelType, code, language, guideType, deviceType, seq },
            }
        );

        return response.data;
    }

    async updateGuide({
        guideId,
        title,
        subtitle,
        modelType,
        code,
        language,
        guideType,
        deviceType,
        seq,
        content,
    }: GuideUpdateReqDto): Promise<SuccessResponse<boolean>> {
        const baseHeaders = this.makeBaseHeader();

        const query: Record<string, any> = { guideId };
        if (title !== undefined) query.title = title;
        if (subtitle !== undefined) query.subtitle = subtitle;
        if (modelType !== undefined) query.modelType = modelType;
        if (code !== undefined) query.code = code;
        if (language !== undefined) query.language = language;
        if (guideType !== undefined) query.guideType = guideType;
        if (deviceType !== undefined) query.deviceType = deviceType;
        if (seq !== undefined) query.seq = seq;

        await axios.options(`${this.BASE_SERVICE_URL}/setting/guide`, { headers: baseHeaders });

        const response = await axios.put<SuccessResponse<boolean>>(`${this.BASE_SERVICE_URL}/setting/guide`, content, {
            headers: { ...baseHeaders, "Content-Type": "text/html" },
            params: query,
        });

        return response.data;
    }

    async deleteGuide({ guideId }: { guideId: string }): Promise<SuccessResponse<boolean>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/setting/guide`, { headers: baseHeaders });

        const response = await axios.delete<SuccessResponse<boolean>>(`${this.BASE_SERVICE_URL}/setting/guide`, {
            headers: baseHeaders,
            params: { guideId },
        });

        return response.data;
    }

    // ===================== Course Image & Price =====================

    async uploadCourseImage({
        imageNum,
        version,
        content,
    }: {
        imageNum: string;
        version: string;
        content: File;
    }): Promise<SuccessResponse<string>> {
        const baseHeaders = this.makeBaseHeader();

        const formData = new FormData();
        formData.append("content", content);

        await axios.options(`${this.BASE_SERVICE_URL}/setting/course-image`, { headers: baseHeaders });

        const response = await axios.post<SuccessResponse<string>>(
            `${this.BASE_SERVICE_URL}/setting/course-image`,
            formData,
            {
                headers: { ...baseHeaders, "Content-Type": "multipart/form-data" },
                params: { imageNum, version },
            }
        );

        return response.data;
    }

    async deleteCourseImage({
        imageNum,
        version,
    }: {
        imageNum: string;
        version: string;
    }): Promise<SuccessResponse<string>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/setting/course-image`, { headers: baseHeaders });

        const response = await axios.delete<SuccessResponse<string>>(`${this.BASE_SERVICE_URL}/setting/course-image`, {
            headers: baseHeaders,
            params: { imageNum, version },
        });

        return response.data;
    }

    async updateCoursePrice({
        deviceId,
        reqDto,
    }: {
        deviceId: string;
        reqDto: CommonRequestBodyDto<CoursePriceUpdateReqDto>;
    }): Promise<SuccessResponse<{ status: string }>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/setting/${deviceId}/course-price`, {
            headers: baseHeaders,
        });

        const response = await axios.put<SuccessResponse<{ status: string }>>(
            `${this.BASE_SERVICE_URL}/setting/${deviceId}/course-price`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getCourseImages({
        imageNum,
        version,
        page,
        pageSize,
    }: {
        imageNum: string | undefined;
        version: string | undefined;
        page: number;
        pageSize: number;
    }): Promise<SuccessResponse<object>> {
        const baseHeaders = this.makeBaseHeader();

        const query: Record<string, any> = {
            page: page,
            pageSize: pageSize,
        };
        if (imageNum) query.imageNum = imageNum;
        if (version) query.version = version;

        await axios.options(`${this.BASE_SERVICE_URL}/devices/course-image`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<object>>(`${this.BASE_SERVICE_URL}/devices/course-image`, {
            headers: baseHeaders,
            params: query,
        });

        return response.data;
    }
}

export default RealSettingsApi;
